import React from "react";

export type IconName =
  | "dashboard" | "users" | "sim" | "link" | "tag" | "phone" | "box"
  | "consumption" | "bell" | "search" | "plus" | "x" | "edit" | "gift"
  | "logout" | "chevDown" | "chevLeft" | "chevRight" | "filter" | "refresh"
  | "send" | "download" | "arrowUp" | "arrowDown" | "dot" | "more" | "menu" | "key" | "shield";

type IconProps = React.SVGAttributes<SVGElement> & {
  name: IconName;
  size?: number;
};

const STROKE: React.SVGAttributes<SVGElement> = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const PATHS: Record<IconName, React.ReactNode> = {
  dashboard: (
    <>
      <rect x="2.5" y="2.5" width="5.5" height="7" rx="1.2" />
      <rect x="10" y="2.5" width="5.5" height="4" rx="1.2" />
      <rect x="10" y="8.5" width="5.5" height="7" rx="1.2" />
      <rect x="2.5" y="11.5" width="5.5" height="4" rx="1.2" />
    </>
  ),
  users: (
    <>
      <circle cx="7" cy="6.5" r="2.6" />
      <path d="M2.5 15c0-2.4 2-4 4.5-4s4.5 1.6 4.5 4" />
      <path d="M12 4.5a2.2 2.2 0 1 1 0 4" />
      <path d="M13 11c1.7.3 2.7 1.5 2.7 3.3" />
    </>
  ),
  sim: (
    <>
      <path d="M5 2.5h5.2L14 6.3V14a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 4 14V4a1.5 1.5 0 0 1 1-1.5z" />
      <rect x="6.5" y="8" width="5" height="5" rx="0.8" />
      <path d="M6.5 10.5h5M9 8v5" />
    </>
  ),
  link: (
    <>
      <path d="M8 10.5a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2L9 5.3" />
      <path d="M10 7.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2L9 12.7" />
    </>
  ),
  tag: (
    <>
      <path d="M2.5 9V3.5a1 1 0 0 1 1-1H9l6 6a1.4 1.4 0 0 1 0 2l-4.5 4.5a1.4 1.4 0 0 1-2 0l-6-6z" />
      <circle cx="6" cy="6" r="1" />
    </>
  ),
  phone: (
    <>
      <rect x="5" y="2" width="8" height="14" rx="1.6" />
      <path d="M8 13.5h2" />
    </>
  ),
  box: (
    <>
      <path d="M2.5 6 9 2.5 15.5 6v6L9 15.5 2.5 12z" />
      <path d="M2.5 6 9 9.5 15.5 6M9 9.5v6" />
    </>
  ),
  consumption: (
    <>
      <path d="M9 2.5a6.5 6.5 0 1 1-6.4 7.5" />
      <path d="M9 2.5V9l4.5 2.5" />
    </>
  ),
  bell: (
    <>
      <path d="M4 12.5h10l-1.2-1.7V8.2a3.8 3.8 0 0 0-7.6 0v2.6z" />
      <path d="M7.5 14.5a1.5 1.5 0 0 0 3 0" />
    </>
  ),
  search: (
    <>
      <circle cx="8" cy="8" r="4.5" />
      <path d="m11.5 11.5 3 3" />
    </>
  ),
  plus: <path d="M9 3.5v11M3.5 9h11" strokeWidth={1.6} />,
  x: <path d="m4.5 4.5 9 9M13.5 4.5l-9 9" strokeWidth={1.6} />,
  edit: (
    <>
      <path d="M3 15h3l8-8-3-3-8 8z" />
      <path d="M10.5 4.5 13.5 7.5" />
    </>
  ),
  gift: (
    <>
      <rect x="2.5" y="7" width="13" height="3" rx="0.6" />
      <path d="M4 10v5.5h10V10M9 7v8.5" />
      <path d="M9 7c-1 0-3 0-3-1.6 0-1 .9-1.4 1.6-1.4 1.4 0 1.4 1.5 1.4 3zM9 7c1 0 3 0 3-1.6 0-1-.9-1.4-1.6-1.4-1.4 0-1.4 1.5-1.4 3z" />
    </>
  ),
  logout: (
    <>
      <path d="M11 3.5H4a1 1 0 0 0-1 1V13.5a1 1 0 0 0 1 1h7" />
      <path d="M8 9h7m0 0-2.5-2.5M15 9l-2.5 2.5" />
    </>
  ),
  chevDown: <path d="m5 7 4 4 4-4" />,
  chevLeft: <path d="m11 4-4 5 4 5" />,
  chevRight: <path d="m7 4 4 5-4 5" />,
  filter: <path d="M3 4.5h12l-4.5 5.5v4l-3 1.5v-5.5z" />,
  refresh: (
    <>
      <path d="M3 9a6 6 0 0 1 10.3-4.2L15 6.5" />
      <path d="M15 3v3.5h-3.5" />
      <path d="M15 9a6 6 0 0 1-10.3 4.2L3 11.5" />
      <path d="M3 15v-3.5h3.5" />
    </>
  ),
  send: (
    <>
      <path d="M15.5 2.5 8 10" />
      <path d="M15.5 2.5 11 15l-3-5-5-3z" />
    </>
  ),
  download: (
    <>
      <path d="M9 2.5v9m0 0L5.5 8M9 11.5 12.5 8" />
      <path d="M3 13.5v1A1 1 0 0 0 4 15.5h10a1 1 0 0 0 1-1v-1" />
    </>
  ),
  arrowUp: <path d="M5 11 9 7l4 4" strokeWidth={1.6} />,
  arrowDown: <path d="M5 7l4 4 4-4" strokeWidth={1.6} />,
  dot: <circle cx="9" cy="9" r="3" fill="currentColor" stroke="none" />,
  more: (
    <g fill="currentColor" stroke="none">
      <circle cx="4" cy="9" r="1.2" />
      <circle cx="9" cy="9" r="1.2" />
      <circle cx="14" cy="9" r="1.2" />
    </g>
  ),
  menu: (
    <>
      <path d="M3 5h12M3 9h12M3 13h12" />
    </>
  ),
  key: (
    <>
      <circle cx="6.5" cy="6.5" r="3.5" />
      <path d="M9.5 9.5 15.5 15.5" />
      <path d="M13 14l1.5-1.5" />
    </>
  ),
  shield: (
    <>
      <path d="M9 2.5L3.5 5v4.5c0 3 2.5 5.5 5.5 6 3-.5 5.5-3 5.5-6V5z" />
      <path d="M6.5 9l1.5 1.5 3-3" />
    </>
  ),
};

export function Icon({ name, size = 18, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 18 18"
      width={size}
      height={size}
      {...STROKE}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
